/* eslint-disable no-null/no-null */
import {
  useEffect, useMemo, useRef, useState,
} from '../../../../../lib/teact/teact';

import type { ApiWalletTransactionMetadata } from '../../../../../api/types';

import { parseTxId } from '../../../../../util/activities';
import { logDebugError } from '../../../../../util/logs';
import { WALLET_METADATA_DIRECTORY_CHANGED_EVENT } from '../../../../../util/walletMetadataDirectory';
import { callApiWithThrow } from '../../../../../api';

import { useStateRef } from '../../../../../hooks/useStateRef';

const EMPTY_METADATA: Record<string, ApiWalletTransactionMetadata> = Object.freeze({});

export type ActivityMetadataCache = {
  byHash: Record<string, ApiWalletTransactionMetadata>;
  loadedHashes: Set<string>;
};

export async function loadActivityMetadata(
  accountId: string, hashes: string[], isCurrent: () => boolean,
  onLocalLoaded?: (metadata: Record<string, ApiWalletTransactionMetadata>) => void,
  cache?: ActivityMetadataCache,
) {
  const localByHash: Record<string, ApiWalletTransactionMetadata> = {};
  const mergeLocal = (remoteByHash: Record<string, ApiWalletTransactionMetadata>) => {
    Object.entries(localByHash).forEach(([hash, local]) => {
      const remoteMetadata = remoteByHash[hash];
      remoteByHash[hash] = {
        ...remoteMetadata, ...local,
        category: local.category === undefined ? remoteMetadata?.category : local.category,
      };
    });
  };
  const missingHashes = cache ? hashes.filter((hash) => !cache.loadedHashes.has(hash)) : hashes;
  const remote = async () => {
    const items: ApiWalletTransactionMetadata[] = [];
    for (let offset = 0; offset < missingHashes.length && isCurrent(); offset += 100) {
      const batchHashes = missingHashes.slice(offset, offset + 100);
      const batch = await callApiWithThrow(
        'fetchWalletTransactionsMetadata', accountId, batchHashes,
      )
        .catch((error) => {
          logDebugError('Transaction history remote metadata load', error);
          return undefined;
        });
      if (!batch) break;
      items.push(...batch);
      if (cache && isCurrent()) {
        batchHashes.forEach((hash) => {
          delete cache.byHash[hash];
        });
        batch.forEach((item) => {
          cache.byHash[item.transaction_hash] = item;
        });
        batchHashes.forEach((hash) => cache.loadedHashes.add(hash));
        mergeLocal(cache.byHash);
      }
    }
    return items;
  };
  const remoteItems = remote();
  const drafts = await callApiWithThrow('fetchWalletTransactionMetadataDrafts', accountId, hashes, false)
    .catch((error) => {
      logDebugError('Transaction history local metadata load', error);
      return [];
    });
  drafts.forEach((draft) => {
    localByHash[draft.transaction_hash] = {
      transaction_hash: draft.transaction_hash,
      description: draft.description,
      counterparty: draft.counterparty_name ? { id: `local:${draft.transaction_hash}`, name: draft.counterparty_name }
        : undefined,
      category: draft.category_id ? { id: draft.category_id, name: '', emoji: '', color: '' }
        : draft.category_id === null ? null : undefined,
      attachments: [], updated_at: new Date(draft.updated_at).toISOString(),
    };
  });
  if (isCurrent()) onLocalLoaded?.(localByHash);
  const items = await remoteItems;
  const byHash = {
    ...cache?.byHash, ...Object.fromEntries(items.map((item) => [item.transaction_hash, item])),
  };
  mergeLocal(byHash);
  if (cache && isCurrent()) Object.assign(cache.byHash, byHash);
  return byHash;
}

export default function useActivityMetadata(
  accountId: string, hashes: string[], isActive?: boolean, currentActivityId?: string, isViewMode?: boolean,
) {
  const [state, setState] = useState({ accountId: '', byHash: EMPTY_METADATA });
  const cacheRef = useRef<ActivityMetadataCache & { accountId: string; activityId?: string }>();
  const hashesRef = useStateRef(hashes);
  const hashesKey = useMemo(() => hashes.join(','), [hashes]);
  useEffect(() => {
    if (!isActive || isViewMode) {
      cacheRef.current = undefined;
      return undefined;
    }
    if (!hashesRef.current.length) return undefined;
    if (!cacheRef.current || cacheRef.current.accountId !== accountId) {
      cacheRef.current = { accountId, activityId: currentActivityId, byHash: {}, loadedHashes: new Set() };
    } else if (cacheRef.current.activityId && cacheRef.current.activityId !== currentActivityId) {
      cacheRef.current.loadedHashes.delete(parseTxId(cacheRef.current.activityId).hash);
    }
    cacheRef.current.activityId = currentActivityId;
    let revision = 0;
    const cache = cacheRef.current;
    const reload = (invalidateCache = false) => {
      const requestRevision = ++revision;
      if (invalidateCache) cache.loadedHashes.clear();
      const isCurrent = () => requestRevision === revision;
      void loadActivityMetadata(accountId, hashesRef.current, isCurrent, (byHash) => {
        if (Object.keys(byHash).length) {
          setState((previous) => ({
            accountId, byHash: { ...(previous.accountId === accountId ? previous.byHash : {}), ...byHash },
          }));
        }
      }, cache).then((byHash) => {
        if (isCurrent()) setState({ accountId, byHash });
      }).catch((error) => logDebugError('Transaction history metadata load', error));
    };
    const refresh = () => reload(true);
    reload();
    window.addEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      revision++;
      window.removeEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [accountId, hashesKey, hashesRef, isActive, currentActivityId, isViewMode]);
  return state.accountId === accountId && !isViewMode ? state.byHash : EMPTY_METADATA;
}
